import { Event } from '@nostr/tools/wasm'
import { isAddressableKind } from '@nostr/tools/kinds'
import { AddressPointer, EventPointer } from '@nostr/tools/nip19'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { getKindDescription } from '@/lib/nostr-kinds-registry'
import { pointerFromATag, pointerFromETag, tagNameEquals } from '@/lib/tag'
import { normalizeUrl } from '@/lib/url'
import { toNote } from '@/lib/link'
import { SecondaryPageLink } from '@/PageManager'
import ProfileRow from '@/components/Profile/ProfileRow'
import RelayCard from '@/components/RelayCard'
import SimpleGroupCard from '@/components/SimpleGroupCard'
import { EmbeddedHashtag } from '@/components/Embedded/EmbeddedHashtag'
import Emoji from '@/components/Emoji'

const META_TAGS = new Set(['d', 'title', 'image', 'description'])

export default function Lists({ event, className }: { event: Event; className?: string }) {
  const { t } = useTranslation()
  const isAddressable = isAddressableKind(event.kind)
  const d = event.tags.find(tagNameEquals('d'))?.[1]
  const title = event.tags.find(tagNameEquals('title'))?.[1]
  const image = event.tags.find(tagNameEquals('image'))?.[1]
  const description = event.tags.find(tagNameEquals('description'))?.[1]
  const [kindDescription, setKindDescription] = useState(`Kind ${event.kind}`)

  useEffect(() => {
    let active = true
    setKindDescription(`Kind ${event.kind}`)
    getKindDescription(event.kind)
      .then((desc) => {
        if (active) setKindDescription(desc)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [event.kind])

  const items = useMemo(() => event.tags.filter((tag) => !META_TAGS.has(tag[0])), [event])

  const [visibleCount, setVisibleCount] = useState(20)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setVisibleCount(20)
  }, [event])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && items.length > visibleCount) {
          setVisibleCount((prev) => Math.min(prev + 20, items.length))
        }
      },
      { root: null, rootMargin: '10px', threshold: 1 }
    )
    const el = bottomRef.current
    if (el) observer.observe(el)
    return () => {
      if (el) observer.unobserve(el)
    }
  }, [visibleCount, items.length])

  const visible = items.slice(0, visibleCount)
  const hasHeader = isAddressable && (title || image || description)
  const hasD = isAddressable && d !== undefined

  return (
    <div className={cn('space-y-3', className)}>
      <div className="text-sm text-muted-foreground font-medium">{kindDescription}</div>
      {hasHeader && (
        <div className="space-y-2">
          {image && (
            <img
              src={image}
              alt={title}
              className="rounded-lg w-full max-h-64 object-cover bg-muted"
            />
          )}
          {title && <h2 className="text-xl font-semibold">{title}</h2>}
          {description && (
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{description}</p>
          )}
        </div>
      )}

      {hasD && <div className="text-xs text-muted-foreground font-mono truncate">#d: {d}</div>}

      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground">{t('Empty list.')}</div>
      ) : (
        <div className="space-y-1">
          {visible.map((tag, i) => (
            <ListItem key={i} tag={tag} />
          ))}
        </div>
      )}

      {items.length > visibleCount && <div ref={bottomRef} className="h-4" />}
    </div>
  )
}

function ListItem({ tag }: { tag: string[] }) {
  const [name, value, ...rest] = tag
  if (!name || !value) return <UnknownTag tag={tag} />

  switch (name) {
    case 'p': {
      return <ProfileRow pubkey={value} />
    }
    case 'e': {
      const ptr = pointerFromETag(tag)
      if (!ptr) return <UnknownTag tag={tag} />
      return <EventPointerLink id={ptr.id} pointer={ptr} />
    }
    case 'a': {
      const ptr = pointerFromATag(tag)
      if (!ptr) return <UnknownTag tag={tag} />
      const id = `${ptr.kind}:${ptr.pubkey}:${ptr.identifier}`
      return <EventPointerLink id={id} pointer={ptr} />
    }
    case 't': {
      return (
        <div className="px-1 py-0.5">
          <EmbeddedHashtag hashtag={value} />
        </div>
      )
    }
    case 'relay':
    case 'r':
    case 'server': {
      const url = normalizeUrl(value)
      if (!url) return <UnknownTag tag={tag} />
      return <RelayCard url={url} />
    }
    case 'group': {
      const groupId = value
      const relay = normalizeUrl(rest[0] ?? '')
      const name = rest[1]
      if (!groupId || !relay) return <UnknownTag tag={tag} />
      return <SimpleGroupCard group={name ? { groupId, relay, name } : { groupId, relay }} />
    }
    case 'emoji': {
      const url = rest[0]
      if (!url) return <UnknownTag tag={tag} />
      return (
        <div className="flex items-center gap-1.5 px-1 py-0.5">
          <Emoji emoji={{ shortcode: value, url }} classNames={{ img: 'size-4' }} />
          <span className="font-mono text-sm">:{value}:</span>
        </div>
      )
    }
    case 'word': {
      return <div className="px-1 py-0.5 text-sm font-mono text-muted-foreground">{value}</div>
    }
    case 'url': {
      return (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="block truncate text-sm text-primary hover:underline px-1 py-0.5"
        >
          {value}
        </a>
      )
    }
    default:
      return <UnknownTag tag={tag} />
  }
}

function EventPointerLink({ id, pointer }: { id: string; pointer: EventPointer | AddressPointer }) {
  const truncated = id.length > 20 ? id.slice(0, 12) + '…' + id.slice(-6) : id
  return (
    <div title={id} className="px-1 py-0.5">
      <SecondaryPageLink
        to={toNote(pointer)}
        className="block truncate font-mono text-sm hover:underline"
      >
        {truncated}
      </SecondaryPageLink>
    </div>
  )
}

function UnknownTag({ tag }: { tag: string[] }) {
  return (
    <div className="px-1 py-0.5 font-mono text-xs text-muted-foreground break-all">
      {JSON.stringify(tag)}
    </div>
  )
}
